import { useState } from 'react';
import { useTrackUnsafe } from '@frameio/shared/segment/hooks';
import {
  AlertDialog,
  AlertDialogPrimaryAction,
  AlertDialogSecondaryAction,
  Box,
  PlaceholderCloudArrowDown,
  Stack,
  Text,
  Toggle,
  ToggleCheckbox,
} from '@frameio/vapor';
import { assetsStorage } from '../../shared';
import TEXT from './generated/TEXT/DownloadModal';

type DownloadModalProps = {
  showDriveApp: boolean;
  onDownloadInDriveApp: () => void;
  onDownloadInTransferApp: () => void;
  onDownloadInBrowser: () => void;
  onCancel: () => void;
  isShowing: boolean;
  segmentId: string;
};

type downloadPreference = 'browser' | 'driveApp' | 'transferApp';

TEXT.DEFINE({
  downloadTitle: 'Download with the Desktop App',
  downloadDetails:
    'Unlock even faster download speeds, plus preserve your folder structures on download.',
  downloadAppReq: 'Requires version 1.2.0 or later',
  downloadInDriveApp: 'Download with Frame.io Drive',
  downloadInTransferApp: 'Download with Frame.io Transfer',
  downloadInBrowser: 'Download in Browser',
  cancelDownload: 'Cancel download',
  rememberDownloadPreference: 'Always use this download method',
});

// Save the user's download preference to localStorage.
// This will bypass the "Download in app" modal
// when the user downloads large amounts of files.
function setDownloadPreference(pref: downloadPreference) {
  assetsStorage.set('downloadPreference', pref);
}

/**
 *
 * The DownloadModal gives users the option to download files in the Transfer app
 * instead of in the web app.
 */
export const DownloadModal = ({
  showDriveApp,
  onDownloadInDriveApp,
  onDownloadInTransferApp,
  onDownloadInBrowser,
  onCancel,
  isShowing,
  segmentId,
}: DownloadModalProps) => {
  const [saveDownloadPreferences, setSaveDownloadPreferences] = useState(false);
  const [prevIsShowing, setPrevIsShowing] = useState(isShowing);
  const track = useTrackUnsafe();

  if (isShowing !== prevIsShowing) {
    setPrevIsShowing(isShowing);
    if (isShowing) {
      setSaveDownloadPreferences(false);
    }
  }

  const actionsConfig = [
    ...(showDriveApp
      ? [
          {
            label: TEXT.downloadInDriveApp,
            preference: 'driveApp' as const,
            trackEvent: 'download-with-drive-app-selected' as const,
            onClick: onDownloadInDriveApp,
            primary: true,
          },
        ]
      : []),
    {
      label: TEXT.downloadInTransferApp,
      preference: 'transferApp' as const,
      trackEvent: 'download-with-transfer-app-selected' as const,
      onClick: onDownloadInTransferApp,
      primary: !showDriveApp,
    },
    {
      label: TEXT.downloadInBrowser,
      preference: 'browser' as const,
      trackEvent: 'download-with-browser-selected' as const,
      onClick: onDownloadInBrowser,
      primary: false,
    },
  ];

  return (
    <AlertDialog
      actions={
        <>
          {actionsConfig.map(
            ({ label, preference, trackEvent, onClick, primary }) => {
              const handleClick = () => {
                if (saveDownloadPreferences) setDownloadPreference(preference);
                track(trackEvent, { transfer_job_id: segmentId });
                onClick();
              };
              const ActionComponent = primary
                ? AlertDialogPrimaryAction
                : AlertDialogSecondaryAction;
              return (
                <ActionComponent key={preference} onClick={handleClick}>
                  {label}
                </ActionComponent>
              );
            }
          )}
          <Box paddingY={16}>
            <Toggle
              checked={saveDownloadPreferences}
              onChange={() =>
                setSaveDownloadPreferences(!saveDownloadPreferences)
              }
            >
              <ToggleCheckbox />
              <Box width={8} />
              <Text variant="body">{TEXT.rememberDownloadPreference}</Text>
            </Toggle>
          </Box>
          <AlertDialogSecondaryAction
            onClick={onCancel}
            size="small"
            variant="tertiary"
          >
            {TEXT.cancelDownload}
          </AlertDialogSecondaryAction>
        </>
      }
      details={
        <Box paddingBottom={8}>
          <Text textAlign="center" variant="body">
            {TEXT.downloadDetails}
          </Text>
        </Box>
      }
      isShowing={isShowing}
      stackActionButtons={true}
      title={
        <Stack>
          <PlaceholderCloudArrowDown />
          {TEXT.downloadTitle}
        </Stack>
      }
      variant="neutral"
    />
  );
};
